import { Children } from 'react';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { NodeAccessApproval, NodeAccessProfileStatus } from '@prisma/client';
import { UseMutationResult } from '@tanstack/react-query';
import { Button, ButtonProps, Descriptions, Form, FormItemProps, Input, ResultProps, Space } from 'antd';
import { AxiosResponse } from 'axios';

import { UpdateProfileApprovalDto } from '@app/domain-t3-interfaces';

import { getDescriptionItems } from './get-description-items';

export type ApprovalFormData = {
  justification: string;
};

export const useApprovalForm = (
  mutate: UseMutationResult<AxiosResponse<NodeAccessApproval, unknown>, unknown, UpdateProfileApprovalDto, unknown>,
  disabled?: boolean,
  onCancel?: () => void,
) => {
  const [form] = Form.useForm<ApprovalFormData>();

  const getJustificationFormItemProps = (): FormItemProps => ({
    children: <Input.TextArea rows={3} maxLength={500} showCount />,
    help: 'Justification is required for rejections.',
    hasFeedback: true,
    required: false,
    label: 'Justification',
    name: 'justification',
    rules: [
      { required: true, message: 'Justification is required for rejections.' },
      { max: 500, message: 'Justification must be less than 500 characters.' },
      { pattern: new RegExp(/^([^<>;]+)$/), message: 'The characters "<", ">", ";" are not allowed.' },
    ],
    validateTrigger: false,
  });

  const getResetButtonProps = (): ButtonProps => ({
    children: 'Reset',
    disabled,
    onClick: () => form.resetFields(),
  });

  const handleApprovalClick = (approve: boolean) => {
    if (approve) {
      const justification = form.getFieldValue('justification');
      mutate.mutate({
        approved: true,
        justification,
      });
    } else {
      form
        .validateFields()
        .then((values) =>
          mutate.mutate({
            approved: false,
            justification: values.justification,
          }),
        )
        .catch(() => {
          return;
        });
    }
  };

  const getApproveButtonProps = (): ButtonProps => ({
    children: 'Approve',
    icon: <CheckOutlined />,
    disabled,
    loading: mutate.isPending,
    onClick: () => handleApprovalClick(true),
    type: 'primary',
  });

  const getRejectButtonProps = (): ButtonProps => ({
    children: 'Reject',
    icon: <CloseOutlined />,
    danger: true,
    type: 'primary',
    disabled,
    loading: mutate.isPending,
    onClick: () => handleApprovalClick(false),
  });

  const handleCancel = () => {
    form.resetFields();
    mutate.reset();
    onCancel && onCancel();
  };

  const getCancelButtonProps = (): ButtonProps => ({
    children: 'Cancel',
    onClick: () => handleCancel(),
  });

  const getGotoNodesButtonProps = (): ButtonProps => ({
    children: 'Go to Approvals',
    type: 'primary',
    onClick: () => handleCancel(),
  });

  const getSuccessResultProps = (props?: ResultProps): ResultProps => ({
    status: 'success',
    title: `${mutate.data?.data.status === NodeAccessProfileStatus.REJECTED ? 'Rejected' : 'Approved'} a profile`,
    subTitle: (
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Descriptions
          bordered
          size="small"
          layout="horizontal"
          labelStyle={{ width: 150 }}
          style={{ textAlign: 'left' }}
          column={1}
          items={mutate.data ? getDescriptionItems(mutate.data.data) : []}
        />
      </Space>
    ),
    ...props,
    extra: [<Button {...getGotoNodesButtonProps()} />, ...Children.toArray(props?.extra)],
  });

  const getFormProps = () => ({
    children: (
      <>
        <Form.Item {...getJustificationFormItemProps()} />
        <Space>
          <Button {...getApproveButtonProps()} />
          <Button {...getRejectButtonProps()} />
          <Button {...getResetButtonProps()} />
          <Button {...getCancelButtonProps()} />
        </Space>
      </>
    ),

    form,
  });

  return { form, getFormProps, getSuccessResultProps };
};
